import { useTranslation } from "react-i18next";
import { Heading, Text, XStack, YStack } from "tamagui";

import {
  ActiveSensorsCard,
  OfflineHubsCard,
  OverdueServiceRequestsCard,
  SensorAlertsCard,
  SensorDistributionCard,
  SensorErrorsCard,
  SensorHealthCard,
  SilentSensorsCard,
  StaleAlertsCard,
} from "../../components/AdminAnalyticsCards";
import { ChartCardSkeleton } from "../../components/ChartCardSkeleton";
import { KPITileSkeleton } from "../../components/KPITileSkeleton";
import { SkeletonBox } from "../../components/SkeletonBox";
import { useFleetAnalytics } from "../../hooks/useAdminAnalytics";

// One fetch, one derived object, every card reads from it — the KPI tiles,
// the health bars and the donut cannot disagree with each other.
const Analytics = () => {
  const { t } = useTranslation();
  const { data, loading, error } = useFleetAnalytics();

  if (loading && !data) {
    return (
      <YStack gap="$5">
        <SkeletonBox height={32} width={220} />
        <XStack gap="$5" width="100%" flexWrap="wrap">
          <KPITileSkeleton />
          <KPITileSkeleton />
          <KPITileSkeleton />
          <KPITileSkeleton />
        </XStack>
        <XStack gap="$5" width="100%" flexWrap="wrap">
          <KPITileSkeleton />
          <KPITileSkeleton />
          <KPITileSkeleton />
        </XStack>
        <XStack gap="$5" width="100%" flexWrap="wrap">
          <ChartCardSkeleton />
          <ChartCardSkeleton />
        </XStack>
      </YStack>
    );
  }

  if (error || !data) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Text color="$statusCritical" fontSize="$10">
          {error ?? t("common.errorOccurred")}
        </Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <Heading
        size="$14"
        fontSize="$14"
        lineHeight="$11"
        $xs={{ fontSize: "$12" }}
      >
        {t("sidebar.analytics")}
      </Heading>
      <XStack gap="$5" width="100%" flexWrap="wrap">
        <OfflineHubsCard count={data.offlineHubCount} />
        <SensorErrorsCard count={data.erroringSensorCount} />
        <SensorAlertsCard count={data.alertingSensorCount} />
        <ActiveSensorsCard count={data.activeSensorCount} />
      </XStack>
      <XStack gap="$5" width="100%" flexWrap="wrap">
        <StaleAlertsCard count={data.staleAlertCount} />
        <SilentSensorsCard count={data.silentUnalertedSensorCount} />
        <OverdueServiceRequestsCard count={data.overdueServiceRequestCount} />
      </XStack>
      <XStack gap="$5" width="100%" flexWrap="wrap">
        <SensorHealthCard {...data} />
        <SensorDistributionCard {...data} />
      </XStack>
    </YStack>
  );
};

export default Analytics;
